/**
 * Thin fetch wrapper over the read API.
 *
 * Thin on purpose. There is no caching layer, no normalisation, no derived
 * state: every screen asks the API a question and renders the answer. The
 * Flutter client will hit the same endpoints, and anything clever here would
 * have to be re-implemented there.
 *
 * Auth is a bearer token typed in once and kept in localStorage. That matches
 * what the server actually offers — a static staff token, no accounts — and
 * pretending otherwise in the client would be theatre.
 */

import type {
  Co2eReportList,
  Co2eResponse,
  EnergyResponse,
  FleetCo2eSummary,
  Fault,
  FleetResponse,
  Location,
  PlacementResult,
  Resolution,
  SeriesResponse,
  UnitDetail,
} from './types';

const BASE = import.meta.env.VITE_API_BASE ?? '';
const TOKEN_KEY = 'equoia.token';
/**
 * A SECOND credential, because the server has two.
 *
 * `EQUOIA_READ_TOKENS` and `EQUOIA_WRITE_TOKENS` are two flat lists and
 * NEITHER IS A SUPERSET OF THE OTHER — a read token is refused on every write,
 * and a write token is refused on every read, including `/v1/fleet`. Verified
 * against production, not assumed.
 *
 * So a person who both reads and writes holds two tokens, and the client has
 * to carry both. Storing one and hoping is how a button comes to do nothing.
 */
const WRITE_TOKEN_KEY = 'equoia.write_token';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
  /** 401/503 both mean "the token you have will not work"; the UI reacts alike. */
  get isAuth(): boolean {
    return this.status === 401 || this.status === 503;
  }
}

export function getToken(): string {
  try {
    return localStorage.getItem(TOKEN_KEY) ?? '';
  } catch {
    return '';
  }
}

export function setToken(token: string): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* private window, or storage blocked — the session just will not persist */
  }
}

export function getWriteToken(): string {
  try {
    return localStorage.getItem(WRITE_TOKEN_KEY) ?? '';
  } catch {
    return '';
  }
}

export function setWriteToken(token: string): void {
  try {
    if (token) localStorage.setItem(WRITE_TOKEN_KEY, token);
    else localStorage.removeItem(WRITE_TOKEN_KEY);
  } catch {
    /* storage blocked */
  }
}

/** Whether this session holds a credential that can write. */
export function canWrite(): boolean {
  return getWriteToken() !== '';
}

/**
 * Is this token accepted for writes?
 *
 * Uses an EXISTING route and creates nothing. `POST /v1/productions` runs its
 * auth dependency BEFORE validating the body, so a deliberately empty body
 * separates the two answers cleanly:
 *
 *     401  the token cannot write
 *     422  the token CAN write; the body was rejected
 *
 * Confirmed against production, including that the row count does not move.
 * There is no read-only write-authorised route to probe instead — every write
 * route writes — so this is the cheapest honest test available.
 */
export async function verifyWriteToken(token: string): Promise<boolean> {
  const response = await fetch(`${BASE}/v1/productions`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: '{}',
  });
  return response.status !== 401 && response.status !== 503;
}

/**
 * Exported so the modeller client can reuse ONE fetch path — same bearer
 * token, same ApiError, same detail extraction. A second client would be a
 * second place for auth to drift.
 */
export async function upload<T>(path: string, form: FormData): Promise<T> {
  /* Content-Type is deliberately NOT set. The browser has to write it itself
     so it can append the multipart boundary; setting `multipart/form-data` by
     hand produces a body FastAPI cannot split, and the failure looks like a
     malformed request rather than a missing boundary. */
  const response = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getWriteToken()}` },
    body: form,
  });
  if (!response.ok) {
    let detail = `${response.status} ${response.statusText}`;
    try {
      const body = (await response.json()) as { detail?: unknown };
      if (typeof body.detail === 'string') detail = body.detail;
    } catch {
      /* not JSON; the status line will have to do */
    }
    throw new ApiError(response.status, detail);
  }
  return (await response.json()) as T;
}

/**
 * SHA-256 of a file, hex, computed in the browser.
 *
 * `crypto.subtle` needs a SECURE CONTEXT — HTTPS or localhost. Production is
 * HTTPS and the dev server is localhost, so both are covered; a plain-HTTP
 * origin would leave `crypto.subtle` undefined, and the caller is told that
 * rather than shown a stack trace about reading a property of undefined.
 *
 * This does not weaken the server gate. The server hashes the bytes it
 * receives and compares them with what was REGISTERED; a wrong digest here
 * produces a refusal there, not an accepted lie.
 */
export async function sha256Hex(file: Blob): Promise<string> {
  if (!globalThis.crypto?.subtle) {
    throw new Error(
      'this browser cannot fingerprint a file on an insecure connection; '
      + 'open the site over https',
    );
  }
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return [...new Uint8Array(digest)]
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  /* Writes carry the write credential, reads the read one. The server keeps
     two lists and refuses the wrong one either way. */
  const method = (init?.method ?? 'GET').toUpperCase();
  const writing = method !== 'GET' && method !== 'HEAD';
  headers.set('Authorization', `Bearer ${writing ? getWriteToken() : getToken()}`);
  if (init?.body) headers.set('Content-Type', 'application/json');

  const response = await fetch(`${BASE}${path}`, { ...init, headers });
  if (!response.ok) {
    let detail = `${response.status} ${response.statusText}`;
    try {
      const body = (await response.json()) as { detail?: unknown };
      if (typeof body.detail === 'string') detail = body.detail;
    } catch {
      /* not JSON; the status line will have to do */
    }
    throw new ApiError(response.status, detail);
  }
  return (await response.json()) as T;
}

function query(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const encoded = search.toString();
  return encoded ? `?${encoded}` : '';
}

export const api = {
  health: () => request<{ ok: boolean; environment: string; database: boolean }>('/v1/health'),

  fleet: () => request<FleetResponse>('/v1/fleet'),

  fleetCo2e: () => request<FleetCo2eSummary>('/v1/fleet/co2e'),

  /* A live PREVIEW: computed on request, stored nowhere. It can disagree with a
   * stored report for the same window, and when it does that disagreement is
   * information — it means a constant changed since the report was stored —
   * so the UI shows both rather than preferring one. */
  co2e: (id: string, opts?: { from?: Date; to?: Date; shootDayLengthH?: number }) =>
    request<Co2eResponse>(
      `/v1/units/${encodeURIComponent(id)}/co2e` +
        query({
          from: opts?.from?.toISOString(),
          to: opts?.to?.toISOString(),
          shoot_day_length_h: opts?.shootDayLengthH,
        }),
    ),

  /* Stored reports. There is deliberately no create() here: storing a report
   * needs the WRITE token, which this client does not hold, and putting a
   * button on it would erode a credential split that was made on purpose.
   * Operators create reports with the write token via script. */
  co2eReports: (id: string, opts?: { includeHistory?: boolean }) =>
    request<Co2eReportList>(
      `/v1/units/${encodeURIComponent(id)}/co2e/reports` +
        query({ include_history: opts?.includeHistory }),
    ),

  unit: (id: string) => request<UnitDetail>(`/v1/units/${encodeURIComponent(id)}`),

  series: (
    id: string,
    opts: { from: Date; to: Date; resolution?: Resolution; metrics?: string[] },
  ) =>
    request<SeriesResponse>(
      `/v1/units/${encodeURIComponent(id)}/series` +
        query({
          from: opts.from.toISOString(),
          to: opts.to.toISOString(),
          resolution: opts.resolution ?? 'auto',
          metrics: opts.metrics?.join(','),
        }),
    ),

  energy: (id: string, opts: { from: Date; to: Date; bucket: 'hour' | 'day' }) =>
    request<EnergyResponse>(
      `/v1/units/${encodeURIComponent(id)}/energy` +
        query({ from: opts.from.toISOString(), to: opts.to.toISOString(), bucket: opts.bucket }),
    ),

  faults: (id: string, opts?: { open?: boolean; from?: Date; to?: Date }) =>
    request<Fault[]>(
      `/v1/units/${encodeURIComponent(id)}/faults` +
        query({
          open: opts?.open,
          from: opts?.from?.toISOString(),
          to: opts?.to?.toISOString(),
        }),
    ),

  ackFault: (id: string, faultId: number, body: { acknowledged_by?: string; note?: string }) =>
    request<Fault>(`/v1/units/${encodeURIComponent(id)}/faults/${faultId}/ack`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  locations: () => request<Location[]>('/v1/locations'),

  createLocation: (body: {
    name: string;
    kind?: string | null;
    address?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    notes?: string | null;
  }) => request<Location>('/v1/locations', { method: 'POST', body: JSON.stringify(body) }),

  placeUnit: (
    id: string,
    body: { location_id: number | null; entered_by?: string; note?: string },
  ) =>
    request<PlacementResult>(`/v1/units/${encodeURIComponent(id)}/location`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};
